import React from 'react';
import { Check } from 'lucide-react';
import { JobApplication, ApplicationTimelineEvent } from '../../services/applicationService';

interface ApplicationTimelineProps {
  application: JobApplication;
}

const ApplicationTimeline: React.FC<ApplicationTimelineProps> = ({ application }) => {
  const getEventColor = (status: string) => {
    switch (status) {
      case 'applied':
        return 'bg-blue-500 border-blue-600';
      case 'test_completed':
        return 'bg-green-500 border-green-600';
      case 'under_review':
        return 'bg-purple-500 border-purple-600';
      case 'shortlisted':
        return 'bg-yellow-500 border-yellow-600';
      case 'rejected':
        return 'bg-red-500 border-red-600';
      case 'interview_scheduled':
        return 'bg-indigo-500 border-indigo-600';
      default:
        return 'bg-gray-400 border-gray-500';
    }
  };

  const isEventComplete = (index: number) => {
    return index < application.timeline.length;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-6">Application Timeline</h3>

      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

        {/* Timeline events */}
        <div className="space-y-8">
          {application.timeline.map((event, index) => (
            <div key={index} className="relative pl-16">
              {/* Event dot */}
              <div className={`
                absolute left-3 top-1
                w-6 h-6 rounded-full border-2
                flex items-center justify-center
                ${getEventColor(event.status)}
              `}>
                <Check className="w-3 h-3 text-white" />
              </div>

              {/* Event content */}
              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl">{event.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{event.description}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {event.timestamp.toLocaleString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional details based on status */}
                {event.status === 'test_completed' && application.totalScore && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Final Score:</span>
                      <span className={`text-lg font-bold ${
                        application.totalScore >= 70 ? 'text-green-600' : 'text-orange-600'
                      }`}>
                        {Math.round(application.totalScore)}%
                      </span>
                    </div>

                    {application.mcqScore !== undefined && application.interviewScore !== undefined && (
                      <div className="grid grid-cols-2 gap-3 mt-2">
                        <div className="text-center p-2 bg-white rounded border border-gray-200">
                          <p className="text-xs text-gray-500">MCQ Score</p>
                          <p className="font-semibold text-gray-900">{Math.round(application.mcqScore)}%</p>
                        </div>
                        <div className="text-center p-2 bg-white rounded border border-gray-200">
                          <p className="text-xs text-gray-500">Interview Score</p>
                          <p className="font-semibold text-gray-900">{Math.round(application.interviewScore)}%</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Pending status */}
        {application.status !== 'rejected' && application.status !== 'interview_scheduled' && (
          <div className="relative pl-16 mt-8">
            <div className="absolute left-3 top-1 w-6 h-6 rounded-full border-2 border-gray-300 bg-white"></div>

            <div className="bg-gray-50 rounded-lg p-4 border border-dashed border-gray-300">
              <p className="text-sm text-gray-500">⏳ Waiting for next update...</p>
              <p className="text-xs text-gray-400 mt-1">We'll notify you when there's progress</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationTimeline;
